import React, { useMemo } from 'react';
import { CanvasTexture } from 'three';
import { ColorScale } from '../../state/sceneStore';
import { getColorFromScale } from '../../utils/colorScale';

interface HeatmapPlaneProps {
  heatmapData: number[][];
  position: [number, number, number];
  size: [number, number];
  heatmapScale: { min: number; max: number };
  colorScale: ColorScale;
}

const HeatmapPlane: React.FC<HeatmapPlaneProps> = ({ heatmapData, position, size, heatmapScale, colorScale }) => {
  const texture = useMemo(() => {
    if (!heatmapData || !Array.isArray(heatmapData) || heatmapData.length === 0 ||
      !Array.isArray(heatmapData[0]) || heatmapData[0].length === 0) {
      return null;
    }

    const rows = heatmapData.length;
    const cols = heatmapData[0].length;

    const canvas = document.createElement('canvas');
    canvas.width = cols;
    canvas.height = rows;
    const context = canvas.getContext('2d');

    if (!context) {
      return null;
    }

    const imageData = context.createImageData(cols, rows);

    const minVal = heatmapScale.min;
    const maxVal = heatmapScale.max;

    const range = maxVal - minVal;

    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        // The heatmap data is ordered from min_y to max_y, but the canvas draws from top to bottom.
        // We need to flip the y-axis by reading from the end of the heatmapData array.
        const val = heatmapData[rows - 1 - i][j];
        const normalized = range > 0 ? (val - minVal) / range : 0;

        // Use the selected color scale
        const { r, g, b } = getColorFromScale(normalized, colorScale);

        const index = (i * cols + j) * 4;
        imageData.data[index] = r;
        imageData.data[index + 1] = g;
        imageData.data[index + 2] = b;
        imageData.data[index + 3] = 255; // Alpha
      }
    }

    context.putImageData(imageData, 0, 0);

    const canvasTexture = new CanvasTexture(canvas);
    canvasTexture.needsUpdate = true;
    return canvasTexture;
  }, [heatmapData, heatmapScale, colorScale]);

  if (!texture) {
    return null;
  }

  return (
    <mesh position={position} rotation={[0, 0, 0]}>
      <planeGeometry args={size} />
      <meshStandardMaterial map={texture} transparent opacity={0.7} />
    </mesh>
  );
};

export default HeatmapPlane;
